import {
    AjustementDePrestationFormalitJsonldAjustementCreate,
    DossierTarifDTOJsonldDonneesFacturationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import {
    DossierOpenSearch,
    useFacturationService,
    useFormaliteService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { useLazyDataLoader, useToaster } from '@europrocurement/l2d-modules/hooks';
import { useBilling, useDossier } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import {
    Acompte,
    ADD_ACOMPTE_CTX,
    ArticlePrefacturation,
    Collection,
    DataPrefacturation,
    Reglement,
    REMOVE_ACOMPTE_CTX,
    REMOVE_REGLEMENT_CTX,
    SET_BILLING_DOSSIER_IDS_CTX,
    TypeFacturation,
    UPDATE_ACOMPTE_CTX,
    UPDATE_ARTICLE_DATA_PREFACTURATION_CTX,
    UPDATE_COLLECTION_CTX,
    UPDATE_REGLEMENT_CTX,
    useCollectionBillingCtx,
    useCollectionBillingCtxDispatch,
} from '@europrocurement/l2d-modules/providers';
import {
    CollectionDeDossierCollectiondossierUpdate,
    ReglementsEtAcomptesCBAcompteUpdate,
    ReglementsEtAcomptesCBJsonldAcompteCreate,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { facturationType } from '../functions';

export const isAcompte = (item: Acompte | Reglement): item is Acompte =>
    item?.isAcompte !== undefined;

const useDossierFacturationActions = () => {
    const { refreshDossierPrefacturation, refreshCollectionInvoices, refreshCollectionEstimates } =
        useLazyDataLoader();
    const { updateDossierArticles } = useDossier();
    const {
        createEstimates: createEstimatesApi,
        createInvoice: createInvoiceApi,
        createProforma: createProformaApi,
    } = useBilling({ origine: 'B2D' });
    const billingDispatch = useCollectionBillingCtxDispatch();
    const { getBillingDossierIds } = useCollectionBillingCtx();
    const { collectionApi, acomptesApi, documentGedApi } = useFormaliteService();
    const { handleApiResponse } = useToaster();
    const { ajustementDePrestationFormalitApi } = useFacturationService();
    const xIdSociete = useSociete();

    const sortableListOnChange = async (
        dataPrefacturation: DataPrefacturation,
        items: Array<ArticlePrefacturation>,
        dossier: DossierTarifDTOJsonldDonneesFacturationRead,
        dossierIdx: number,
    ) => {
        if (!dataPrefacturation) {
            throw new Error('dataPrefacturation is null');
        }

        if (!dossier.id) {
            throw new Error('dossier sans id');
        }

        if (!dataPrefacturation.dossiers || !dataPrefacturation.dossiers[dossierIdx]) {
            throw new Error('Prefacturation sans dossiers');
        }

        // changer le rang de tous les items
        const newItems = items.map((item, index) => ({
            ...item,
            rang: index * 10 + 10,
        }));
        // constuire un nouveau dossier
        const newDossiers = [...(dataPrefacturation.dossiers || [])];

        newDossiers.splice(dossierIdx, 1, {
            ...dataPrefacturation.dossiers[dossierIdx],
            articles: newItems,
        });

        if (billingDispatch) {
            newItems.forEach((item) => {
                billingDispatch({
                    type: UPDATE_ARTICLE_DATA_PREFACTURATION_CTX,
                    payload: item,
                });
            });
        }

        if (
            newDossiers[dossierIdx].articles === undefined ||
            newDossiers[dossierIdx].articles.length === 0
        ) {
            throw new Error('dossier sans articles');
        }

        const articlesUpdateDossierArticles = newDossiers[dossierIdx].articles.map((article) => ({
            id: article.id,
            rang: article.rang,
            libelle: article.libelle !== undefined ? article.libelle : null,
        }));

        await updateDossierArticles(dossier.id, articlesUpdateDossierArticles);
    };

    const updateProvisionAttendue = async (collection: Collection, amount: number) => {
        if (billingDispatch) {
            billingDispatch({
                type: UPDATE_COLLECTION_CTX,
                payload: { ...collection, acompteAttendu: amount },
            });
        }

        const collectionDeDossierCollectiondossierUpdate: CollectionDeDossierCollectiondossierUpdate =
            {
                acompteAttendu: String(amount),
            };

        const createRequest = collectionApi.apiCollectionsDossiersIdPatch({
            id: collection.id as unknown as string,
            collectionDeDossierCollectiondossierUpdate,
        });

        await handleApiResponse(createRequest);
    };

    const createAcompte = async (
        collection: Collection,
        acompte: Acompte,
        files?: File[],
        dossier?: DossierOpenSearch,
    ) => {
        try {
            const reglementsEtAcomptesCBJsonldAcompteCreate: ReglementsEtAcomptesCBJsonldAcompteCreate =
                {
                    mode: acompte.mode,
                    montant: String(acompte.montant),
                    reference: acompte.reference,
                    origine: acompte.origine,
                    detailsReglements: acompte.detailsReglements.map((item) => ({
                        idCollection: String(collection.id),
                        ttc: String(item.ttc),
                    })),
                    note: acompte.note,
                    dreception: acompte.dreception?.toDateString(),
                    usercreation: acompte.usercreation,
                };

            const createRequest = await acomptesApi.apiAcomptesPost({
                reglementsEtAcomptesCBJsonldAcompteCreate,
            });

            if (billingDispatch) {
                billingDispatch({
                    type: ADD_ACOMPTE_CTX,
                    payload: { ...acompte, id: createRequest.data.id as number },
                });
            }

            if (
                files &&
                files.length > 0 &&
                createRequest.data.id &&
                dossier?.prescripteurId &&
                dossier?.id
            ) {
                await Promise.all(
                    files.map((file) =>
                        documentGedApi.apiDocumentgedFilePost({
                            file,
                            fileName: file.name,
                            idPrescripteur: dossier.prescripteurId as number,
                            idSociete: xIdSociete as number,
                            idDossierFormalite: dossier.id as number,
                            idReglement: createRequest.data.id,
                        }),
                    ),
                );
            }
            return { ...acompte, id: createRequest.data.id as number };
        } catch (error) {
            console.error('Erreur lors de la création du règlement:', error);

            throw error;
        }
    };

    const deleteAcompteOrReglement = async (item: Acompte | Reglement) => {
        const deleteRequest = acomptesApi.apiAcomptesIdDelete({
            id: item.id as unknown as string,
        });

        await handleApiResponse(deleteRequest);

        if (billingDispatch) {
            if (isAcompte(item)) {
                billingDispatch({ type: REMOVE_ACOMPTE_CTX, payload: item.id });
            } else {
                billingDispatch({ type: REMOVE_REGLEMENT_CTX, payload: item.id });
            }
        }
    };

    const updateAcompteOrReglement = async (item: Acompte | Reglement) => {
        const reglementsEtAcomptesCBAcompteUpdate: ReglementsEtAcomptesCBAcompteUpdate = {
            dreception: item.dreception?.toDateString(),
            reference: item.reference,
            note: item.note,
        };

        const updateRequest = acomptesApi.apiAcomptesIdPatch({
            id: item.id as unknown as string,
            reglementsEtAcomptesCBAcompteUpdate,
        });

        await handleApiResponse(updateRequest);

        if (billingDispatch) {
            if (isAcompte(item)) {
                billingDispatch({ type: UPDATE_ACOMPTE_CTX, payload: item });
            } else {
                billingDispatch({ type: UPDATE_REGLEMENT_CTX, payload: item });
            }
        }
    };

    const updateFacturationType = async (
        collection: Collection,
        newTypeFacturation: facturationType,
    ) => {
        const collectionDeDossierCollectiondossierUpdate: CollectionDeDossierCollectiondossierUpdate =
            {
                typeFacturation: newTypeFacturation,
            };

        const createRequest = collectionApi.apiCollectionsDossiersIdPatch({
            id: String(collection.id),
            collectionDeDossierCollectiondossierUpdate,
        });

        await handleApiResponse(createRequest, () => {
            if (billingDispatch) {
                billingDispatch({
                    type: UPDATE_COLLECTION_CTX,
                    payload: {
                        ...collection,
                        typeFacturation: newTypeFacturation as unknown as TypeFacturation,
                    },
                });
            }
        });
    };

    const createEstimates = async (collections: Collection[]): Promise<void> => {
        const collectionIds = collections.map((c) => c.id);
        const dossierIds = collections.map((c) => c.dossierIds).flat();

        await createEstimatesApi(collectionIds);

        await Promise.all([
            refreshCollectionEstimates(collectionIds),
            refreshCollectionInvoices(collectionIds),
            refreshDossierPrefacturation(dossierIds),
        ]);
    };

    const createInvoice = async (
        dataPrefacturation: DataPrefacturation,
        acomptes: Acompte[],
        collections: Collection[],
    ): Promise<void> => {
        const collectionIds = collections.map((c) => c.id);
        const dossierIds = collections.map((c) => c.dossierIds).flat();
        const lignes =
            dataPrefacturation?.dossiers.flatMap(
                (dossier) =>
                    dossier.articles
                        ?.filter((article) => article.articleFacturable)
                        .map((article) => ({
                            idFproPrestaFormArt: article.id as unknown as string,
                        })) ?? [],
            ) ?? [];

        await createInvoiceApi(
            lignes,
            acomptes
                .map((a) => a.detailsReglements.map((dr) => dr.id))
                .flat() as unknown[] as string[],
        );

        await Promise.all([
            refreshCollectionEstimates(collectionIds),
            refreshCollectionInvoices(collectionIds),
            refreshDossierPrefacturation(dossierIds, true),
        ]);
    };

    const createProforma = async (
        dataPrefacturation: DataPrefacturation,
        acomptes: Acompte[],
        collections: Collection[],
    ): Promise<void> => {
        const collectionIds = collections.map((c) => c.id);
        const dossierIds = collections.map((c) => c.dossierIds).flat();
        const lignes =
            dataPrefacturation?.dossiers.flatMap(
                (dossier) =>
                    dossier.articles
                        ?.filter((article) => article.articleFacturable)
                        .map((article) => ({
                            idFproPrestaFormArt: article.id as unknown as string,
                        })) ?? [],
            ) ?? [];

        await createProformaApi(
            lignes as unknown as Parameters<typeof createProformaApi>[0],
            acomptes
                .map((a) => a.detailsReglements.map((dr) => dr.id))
                .flat() as unknown[] as string[],
        );

        await Promise.all([
            refreshCollectionEstimates(collectionIds),
            refreshCollectionInvoices(collectionIds),
            refreshDossierPrefacturation(dossierIds, true),
        ]);
    };

    const ajustInvoice = async (
        dataPrefacturation: DataPrefacturation,
        acomptes: Acompte[],
        collections: Collection[],
    ) => {
        if (!billingDispatch) return;

        const current = getBillingDossierIds();
        const dossierIds = collections.map((c) => c.dossierIds).flat();
        const articles =
            dataPrefacturation?.dossiers &&
            dataPrefacturation.dossiers
                .filter((dossierSelected) => current.indexOf(Number(dossierSelected.id)) !== -1)
                .flatMap(
                    (prefacturationdossier) =>
                        prefacturationdossier.articles
                            ?.filter((articleSelected) => articleSelected.articleFacturable)
                            .map((article) => article.id as number) || [],
                );

        const acompteIds: number[] = acomptes.flatMap((acompte) =>
            (acompte.detailsReglements ?? []).map((detail) => {
                if (detail.id === undefined || detail.id === null) {
                    throw new Error('detail sans id');
                }
                return detail.id;
            }),
        );

        const ajustementDePrestationFormalitJsonldAjustementCreate: AjustementDePrestationFormalitJsonldAjustementCreate =
            {
                articles,
                acomptes: acompteIds,
            };
        const ajustementRequest =
            ajustementDePrestationFormalitApi.apiAjustementArticlesPrestationPost({
                xIdSociete,
                ajustementDePrestationFormalitJsonldAjustementCreate,
            });

        await handleApiResponse(ajustementRequest);

        refreshDossierPrefacturation(dossierIds);
    };

    const onChangeDossiersSelected = async (dossierIds: number[], id: number, checked: boolean) => {
        if (!billingDispatch) return;

        const current = getBillingDossierIds();
        const updated = checked ? [...current, id] : current.filter((d) => d !== id);

        billingDispatch({ type: SET_BILLING_DOSSIER_IDS_CTX, payload: updated });
        await refreshDossierPrefacturation(dossierIds, false, updated);
    };

    const onUpdateArticles = async (dossierIds: number[]) => {
        await refreshDossierPrefacturation(dossierIds, true);
    };

    return {
        sortableListOnChange,
        ajustInvoice,
        onChangeDossiersSelected,
        onUpdateArticles,
        updateProvisionAttendue,
        createAcompte,
        deleteAcompteOrReglement,
        updateAcompteOrReglement,
        updateFacturationType,
        createEstimates,
        createInvoice,
        createProforma,
    };
};

export default useDossierFacturationActions;
